<template>
	<div class="leaderboard">
		<div class="title-wrapper">
			<span class="bracket-wrapper">
				<img class="bracket" src="~@/assets/celtic_bracket.png" />
			</span>
			<h1 class="title">
				Leaderboard
			</h1>
			<span class="bracket-wrapper left">
				<img class="bracket flipped" src="~@/assets/celtic_bracket.png" />
			</span>
		</div>
		<div class="loading-leaderboard" v-if="!loaded">
			Loading<span class="ellipsis animated-ellipsis"></span>
		</div>
		<div class="leaderboard-filters" v-if="loaded">
			<div class="filters-content">
				<div class="leaderboard-type">
					<div class="form-group seasons-wrapper">
						<div class="form-control-wrapper select-wrapper select-with-icon-wrapper">
							<select class="form-control select" name="season" v-model="selectedLeaderboard">
								<option v-for="option in leaderboardOptions" :key="`leaderboard-option-${option.value}`"
									:value="option.value">
									{{ option.label }}
								</option>
							</select>
							<fa-icon icon="sort-down" class="icon" />
						</div>
						<div class="season-dates"
							v-if="!['none', 'all-time'].includes(selectedLeaderboard) && selectedSeasonData">
							({{ formatDate(selectedSeasonData.startAt, 'MMM-DD-YYYY') }} -
							{{ formatDate(selectedSeasonData.endAt, 'MMM-DD-YYYY') }})
						</div>
					</div>
				</div>
				<div class="leaderboard-notice" v-if="leaders.length">
					*Only the top {{ leaders.length }} players
					{{ selectedLeaderboard === 'none' ? 'active in the last two years' : '' }}
					with at least 15 games appear in the leaderboard.
				</div>
			</div>
		</div>
		<table class="leaderboard-table"
			v-if="loaded && leaders.length && ['none', 'all-time'].includes(selectedLeaderboard)">
			<thead>
				<tr class="table-header">
					<th>
						#
					</th>
					<th>
						Player
					</th>
					<th class="hide-on-mobile">
						Last Activity
					</th>
					<th>
						Wins
					</th>
					<th>
						Losses
					</th>
					<th class="hide-on-mobile">
						Total games
					</th>
					<th class="hide-on-mobile">
						Win Rate
					</th>
					<th>
						<span class="skill-rating-header">
							<span class="sr-tooltip-wrapper" @click="onShowSrModal">
								<fa-icon icon="question-circle" class="sr-tooltip-icon" />
							</span>
						</span>
					</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="(leader, index) in leaders" :key="`leader-${leader.id}`">
					<td>
						{{ index + 1 }}
					</td>
					<td class="username">
						<router-link :to="`/matches/${encodeLabel(leader.username)}`" class="link" target="_blank">
							{{ leader.username }}
						</router-link>
					</td>
					<td class="hide-on-mobile">
						{{ formatDate(leader.lastRankedActiveAt) }}
					</td>
					<td>
						{{ leader.rankedWins }}
					</td>
					<td>
						{{ leader.rankedLosses }}
					</td>
					<td class="hide-on-mobile">
						{{ leader.rankedGames }}
					</td>
					<td class="hide-on-mobile">
						{{ formatWinRate(leader.rankedWinRate) }}
					</td>
					<td>
						<rank :skillRating="leader.skillRating" />
						<span class="skill-rating-score">
							{{ leader.skillRating }}
						</span>
					</td>
				</tr>
			</tbody>
		</table>
		<table class="leaderboard-table"
			v-if="loaded && seasonLeaders.length && !['none', 'all-time'].includes(selectedLeaderboard)">
			<thead>
				<tr class="table-header">
					<th>
						#
					</th>
					<th>
						Player
					</th>
					<th>
						Wins
					</th>
					<th>
						Losses
					</th>
					<th class="hide-on-mobile">
						Total games
					</th>
					<th class="hide-on-mobile">
						Win Rate
					</th>
					<th>
					</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="(seasonLeader, index) in seasonLeaders" :key="`season-leader-${index}`">
					<td>
						{{ index + 1 }}
					</td>
					<td class="username">
						<router-link :to="`/matches/${encodeLabel(seasonLeader.user.username)}`" class="link"
							target="_blank">
							{{ seasonLeader.user.username }}
						</router-link>
					</td>
					<td>
						{{ seasonLeader.rankedWins }}
					</td>
					<td>
						{{ seasonLeader.rankedGames - seasonLeader.rankedWins }}
					</td>
					<td class="hide-on-mobile">
						{{ seasonLeader.rankedGames }}
					</td>
					<td class="hide-on-mobile">
						{{ formatWinRate(seasonLeader.rankedWinRate) }}
					</td>
					<td>
						<rank :skillRating="seasonLeader.skillRating" />
						<span class="skill-rating-score">
							{{ seasonLeader.skillRating }}
						</span>
					</td>
				</tr>
			</tbody>
		</table>
		<div class="leaderboard-placeholder" v-if="loaded && !leaders.length">
			No information available.
		</div>
		<skill-rating-modal :show="showSrModal" :onCloseCallback="onCloseSrModal" />
	</div>
</template>

<script>
import moment from 'moment';

export default {
	name: 'Leaderboard',
	created() {
		this.$store.dispatch('getLeaderboard', this.activeLeaderboarFromDate);
		this.$store.dispatch('getSeasons');
	},
	data() {
		return {
			activityDateRange: null,
			showSrModal: false,
			selectedLeaderboard: 'none',
		};
	},
	watch: {
		activityDateRange(currentDate, prevDate) {
			const hasRange = Array.isArray(currentDate) && currentDate[0] && currentDate[1];
			const hadRange = Array.isArray(prevDate) && prevDate[0] && prevDate[1];

			if (hasRange) {
				this.onActivityDateChange(`${moment(currentDate[0]).format()}~${moment(currentDate[1]).format()}`);
			} else if (hadRange && !hasRange) {
				this.onActivityDateChange(null);
			}
		},
		selectedLeaderboard(value) {
			const season = Number(value);

			if (!isNaN(season)) {
				this.$store.dispatch('getSeasonLeaderboard', season);
				return;
			}

			if (value === 'all-time') {
				this.$store.dispatch('getLeaderboard');
			} else if (value === 'none') {
				this.$store.dispatch('getLeaderboard', this.activeLeaderboarFromDate);
			}
		},
	},
	computed: {
		activeLeaderboarFromDate() {
			return moment().subtract(2, 'year').format();
		},
		userId() {
			return this.$store.state.loginState.userId;
		},
		leaders() {
			return this.$store.state.leaderboardState.leaders;
		},
		loaded() {
			return this.$store.state.leaderboardState.loaded;
		},
		seasons() {
			const seasons = this.$store.state.leaderboardState.seasons;
			return Array.isArray(seasons) ? seasons : [];
		},
		leaderboardOptions() {
			const staticOptions = [
				{ value: 'none', label: 'Main Leaderboard' },
				{ value: 'all-time', label: 'All-Time Leaderboard' },
			];

			const seasonOptions = this.seasons.map(season => ({
				value: String(season.number),
				label: `Season ${season.number}`,
			}));

			return [...staticOptions, ...seasonOptions];
		},
		seasonLeaders() {
			return this.$store.state.leaderboardState.seasonLeaders || [];
		},
		selectedSeasonData() {
			if (['none', 'all-time'].includes(this.selectedLeaderboard)) {
				return null;
			}

			const selectedSeasonNumber = Number(this.selectedLeaderboard);

			if (Number.isNaN(selectedSeasonNumber)) {
				return null;
			}

			return this.seasons.find(season => Number(season.number) === selectedSeasonNumber) || null;
		},
	},
	methods: {
		onActivityDateChange(date) {
			this.$store.dispatch('getLeaderboard', date);
		},
		formatWinRate(winRate) {
			return `${winRate.toFixed(2)}%`;
		},
		onShowSrModal() {
			this.showSrModal = true;
		},
		onCloseSrModal() {
			this.showSrModal = false;
		},
		encodeLabel(label) {
			return encodeURIComponent(label);
		},
		formatDate(date, format = 'YYYY-MMM-DD') {
			return date ? moment(date).format(format) : 'N/A';
		},
	},
};
</script>

<style scoped lang="scss">
@use "@/styles/styles.scss" as *;

.leaderboard {
	padding: 25px;

	.title-wrapper {

		.title {
			font-size: 2.5rem;
			max-width: 275px;
		}
	}

	.leaderboard-filters {
		display: flex;
		justify-content: center;
		margin: 15px auto;

		.filters-content {
			display: flex;
			flex-direction: column;
			justify-content: center;
			margin-top: 5px;
		}

		.filter-label {
			font-size: .75rem;
			margin-right: 10px;
		}

		.filter-group {
			align-self: center;
			display: flex;

			>* {
				align-self: center;
			}
		}

		.activity-filter {
			flex-direction: column;

			.filter-label {
				margin-bottom: 5px;
			}
		}

		>* {
			align-self: center;
		}
	}

	.seasons-wrapper {
		align-items: center;

		.season-dates {
			margin-left: 10px;
		}
	}

	.leaderboard-type {
		margin-bottom: 5px;

		.select-wrapper {
			position: relative;

			.icon {
				position: absolute;
				color: $dark-blue;
				right: 10px;
				top: 9px;
			}
		}

		.select-with-icon-wrapper {
			.select {
				padding-right: 30px;
			}
		}
	}

	.leaderboard-notice {
		margin-top: 10px;
		text-align: center;
		font-size: .9em;
	}

	.hide-on-mobile {
		display: none;
	}

	.loading-leaderboard,
	.leaderboard-placeholder {
		text-align: center;
		margin: 15px auto;
	}

	.stats-wrapper {
		text-align: center;
		margin-top: 15px;

		.stats {
			list-style-type: none;
			padding: 0;
			margin: 0;

			li {
				display: inline-block;

				&:not(:last-of-type) {
					margin-right: 15px;
				}
			}
		}
	}

	.leaderboard-table {
		background: rgba(255, 255, 255, .85);
		margin: 15px auto;
		border: 1px solid $light-gray;

		th {
			color: $dark-brown;
			font-weight: bold;
		}

		td,
		th {
			border-top: 1px solid $light-gray;
			padding: 10px 6px;
			text-align: center;
			vertical-align: middle;
		}

		.rank {
			margin-right: 5px;
		}

		.username {

			.link {
				color: $dark-blue;
			}
		}
	}

	.skill-rating-header {

		&:before {
			content: 'SR';
		}
	}

	.sr-tooltip-wrapper {
		position: relative;

		.sr-tooltip-icon {
			cursor: pointer;
			font-size: .8em;
			margin-left: 3px;
			vertical-align: top;
		}
	}

	.modal {

		&.skill-rating {
			font-size: .9em;
			min-width: 350px;
			height: 80%;

			.modal-content {
				height: 100%;
				overflow: auto;
			}

			.modal-title {
				text-align: center;
			}

			ul {
				margin-left: 30px;
				margin-top: 15px;
				list-style-type: disc;

				li {
					margin-bottom: 10px;

					&:last-of-type {
						margin-bottom: 0;
					}
				}
			}

			.sr-breakdown {
				margin-top: 5px;
			}

			.close-modal-btn {
				cursor: pointer;
				position: absolute;
				right: 20px;
				top: 20px;
			}

			.example-note {
				display: inline-block;
				margin-top: 10px;
			}

			.rank {
				vertical-align: middle;
			}
		}
	}

	.equation {
		display: flex;
		width: 100%;
		margin: 10px 0 0 12px;

		>* {
			align-self: center;
		}

		.fraction {
			display: flex;
			flex-direction: column;
		}

		.equals-sign {
			margin: 0 15px;
		}

		.numerator {
			text-align: center;
			border-bottom: 1px solid black;
		}

		.denominator {
			display: flex;
			align-self: center;

			>* {
				align-self: center;
			}
		}

		.power {
			margin-top: 9px;
		}

		sup {
			font-size: .65em;
			vertical-align: super;
			margin-left: 2px;
		}

		sub {
			vertical-align: sub;
		}

	}
}

@media only screen and (min-width: $screen-sm) {

	.leaderboard {

		.title-wrapper {

			.title {
				font-size: 2.8em;
			}
		}

		.leaderboard-filters {

			.filters-content {
				align-items: center;
			}

			.activity-filter {
				flex-direction: row;

				.filter-label {
					margin-bottom: 0;
				}
			}
		}

		.hide-on-mobile {
			display: table-cell;
		}

		.leaderboard-table {

			td,
			th {
				padding: 15px 20px;
			}
		}

		.skill-rating-header {

			&:before {
				content: 'Skill Rating';
			}
		}

		.modal {

			&.skill-rating {
				min-width: 500px;
			}
		}
	}
}
</style>
